// The dashboard's drawing parts: the widget card, the headline figure of a
// measure, its line, a ring, a split bar, list rows, skeletons and empty states.
// Flat: hairline borders and no shadows. Navy for data; colour only where it
// means something (a rise, a fall, a failure). Size carries the hierarchy: one
// large figure per card, anything else small beside or under it.

import React, { createContext, useContext, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { AlertTriangle, ArrowDownRight, ArrowRight, ArrowUpRight, GripVertical, MoreVertical, RotateCw, Trash2 } from 'lucide-react';

import { NUMBER, PERCENT, change, t } from './DashboardData';

// ------------------------------------------------------------------ the card

// What the grid tells a widget about its tile: whether it can be moved (its header
// is the handle) and whether it has a menu, and what removing it does.
export const TileContext = createContext(null);
// The grid shows a shimmer until every widget has its first answer; each read that
// is still waiting holds it up.
export const ReadyContext = createContext(null);

// The widget's menu, top right: for now, removing the widget.
function WidgetMenu({ name, onRemove }) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (event.type === 'keydown' ? event.key === 'Escape' : !(box.current && box.current.contains(event.target))) setOpen(false);
    };
    window.addEventListener('pointerdown', close);
    window.addEventListener('keydown', close);
    return () => { window.removeEventListener('pointerdown', close); window.removeEventListener('keydown', close); };
  }, [open]);
  return (
    <div className="xo-dash-menu" ref={box}>
      <button type="button" className="xo-dash-menu-button" aria-haspopup="menu" aria-expanded={open}
        aria-label={t('edit.menu', { name })} title={t('edit.menu', { name })} onClick={() => setOpen((shown) => !shown)}>
        <MoreVertical aria-hidden="true" />
      </button>
      {open && (
        <div className="xo-dash-menu-list" role="menu">
          <button type="button" role="menuitem" className="is-danger" onClick={() => { setOpen(false); onRemove(); }}>
            <Trash2 aria-hidden="true" />{t('edit.remove_widget')}
          </button>
        </div>
      )}
    </div>
  );
}

// A card's header row: the drag grip when the tile moves, the title, and the menu.
function useTile(name) {
  const tile = useContext(TileContext);
  return {
    grip: tile && tile.movable ? ' xo-dash-grip' : '',
    mark: tile && tile.movable ? <GripVertical className="xo-dash-grip-mark" aria-label={t('edit.drag', { name })} /> : null,
    menu: tile && tile.editable ? <WidgetMenu name={name} onRemove={tile.onRemove} /> : null,
  };
}

export function WidgetCard({ title, detail, to, aside = null, children, className = '', tone = null }) {
  const tile = useTile(title);
  return (
    <section className={`xo-dash-card ${className}${tone ? ` is-${tone}` : ''}`}>
      <header className={`xo-dash-card-head${tile.grip}`}>
        {tile.mark}
        <div className="xo-dash-card-title">
          <h2>{title}</h2>
          {detail && <p>{detail}</p>}
        </div>
        {aside}
        {to && (
          <Link className="xo-dash-card-link" to={to} aria-label={t('card.open', { title })} title={t('card.open', { title })}>
            <ArrowUpRight aria-hidden="true" />
          </Link>
        )}
        {tile.menu}
      </header>
      <div className="xo-dash-card-body">{children}</div>
    </section>
  );
}

// Draws a widget's answer once it has one: a skeleton before, a short error with
// a retry when the first read failed. A later failed refresh keeps the last answer.
export function Loaded({ resource, skeleton = <SkeletonList />, children }) {
  const ready = useContext(ReadyContext);
  const id = useId();
  const waiting = resource.data === null && !resource.error;
  useEffect(() => {
    if (!ready) return undefined;
    if (waiting) ready.hold(id); else ready.release(id);
    return () => ready.release(id);
  }, [ready, id, waiting]);
  if (resource.data === null) {
    if (resource.error) return <WidgetError message={resource.error} onRetry={resource.refresh} />;
    return skeleton;
  }
  return children(resource.data);
}

export function WidgetError({ message, onRetry }) {
  return (
    <div className="xo-dash-error" role="alert">
      <AlertTriangle aria-hidden="true" />
      <div><strong>{t('card.failed')}</strong><span>{message}</span></div>
      <button type="button" className="xo-dash-ghost-button" onClick={onRetry}><RotateCw aria-hidden="true" />{t('card.retry')}</button>
    </div>
  );
}

export function EmptyNote({ icon: Icon, title, detail }) {
  return (
    <div className="xo-dash-empty">
      {Icon && <Icon className="xo-dash-empty-icon" aria-hidden="true" />}
      <strong>{title}</strong>
      {detail && <p>{detail}</p>}
    </div>
  );
}

// ------------------------------------------------------------------ skeletons

export function SkeletonKpi() {
  return (
    <div className="xo-dash-skeleton is-kpi" aria-busy="true" aria-label={t('card.loading')}>
      <i style={{ width: '38%' }} /><i style={{ width: '52%', height: 30 }} /><i style={{ width: '44%' }} /><i className="is-block" />
    </div>
  );
}

export function SkeletonChart() {
  const heights = [38, 52, 44, 70, 58, 82, 64, 90, 72, 60, 78, 94];
  return (
    <div className="xo-dash-skeleton is-chart" aria-busy="true" aria-label={t('card.loading')}>
      <i style={{ width: '30%', height: 26 }} />
      <div className="xo-dash-skeleton-bars">{heights.map((height, index) => <i key={index} style={{ height: `${height}%` }} />)}</div>
    </div>
  );
}

export function SkeletonList({ rows = 4 }) {
  return (
    <div className="xo-dash-skeleton is-list" aria-busy="true" aria-label={t('card.loading')}>
      {Array.from({ length: rows }, (_, index) => (
        <div key={index}><span><i style={{ width: `${68 - index * 9}%` }} /><i style={{ width: `${42 + index * 6}%` }} /></span><i className="is-end" /></div>
      ))}
    </div>
  );
}

export function SkeletonMap() {
  return (
    <div className="xo-dash-skeleton is-map" aria-busy="true" aria-label={t('card.loading')}>
      <i className="is-block" />
    </div>
  );
}

// ------------------------------------------------------------------ numbers

// A number that counts to its new value when a live refresh changes it.
export function Counter({ value, format = (number) => NUMBER.format(number) }) {
  const reduce = useReducedMotion();
  const [shown, setShown] = useState(value);
  const from = useRef(value);
  useEffect(() => {
    if (reduce || !Number.isFinite(value) || !Number.isFinite(from.current) || from.current === value) {
      from.current = value;
      setShown(value);
      return undefined;
    }
    const start = performance.now();
    const origin = from.current;
    let frame = 0;
    const step = (time) => {
      const progress = Math.min(1, (time - start) / 700);
      const eased = 1 - ((1 - progress) ** 3);
      setShown(origin + ((value - origin) * eased));
      if (progress < 1) frame = window.requestAnimationFrame(step);
      else from.current = value;
    };
    frame = window.requestAnimationFrame(step);
    return () => { window.cancelAnimationFrame(frame); from.current = value; };
  }, [value, reduce]);
  return <>{format(Number.isInteger(value) ? Math.round(shown) : shown)}</>;
}

// A rise or fall against the period before. `goodWhen` says which way is good.
export function Delta({ current, previous, goodWhen = 'up', label }) {
  const ratio = change(current, previous);
  if (ratio === null) return <span className="xo-dash-delta is-none">{t('kpi.no_comparison')}</span>;
  const flat = Math.abs(ratio) < 0.005;
  const up = ratio > 0;
  const tone = flat ? 'flat' : (up === (goodWhen === 'up') ? 'good' : 'bad');
  const Icon = flat ? ArrowRight : up ? ArrowUpRight : ArrowDownRight;
  const text = flat ? t('kpi.flat') : `${up ? '+' : '−'}${PERCENT.format(Math.abs(ratio))}`;
  return (
    <span className={`xo-dash-delta is-${tone}`} title={label}>
      <Icon aria-hidden="true" />{text}
    </span>
  );
}

// A headline measure: its name, a hint (the period, the scope or the currency) and
// the way to its page along the top, then one large figure, how it moved, and its
// month as a chart with its days along the bottom. A measure with no history to
// draw shows how it splits instead (`stages`).
export function KpiCard({ title, hint = null, hintTitle = null, to = null, children }) {
  const tile = useTile(title);
  return (
    <section className="xo-dash-card is-kpi">
      <header className={`xo-dash-kpi-top${tile.grip}`}>
        {tile.mark}
        <h2 className="xo-dash-kpi-label">{title}</h2>
        {hint && <span className="xo-dash-kpi-hint" title={hintTitle || hint}>{hint}</span>}
        {to && (
          <Link className="xo-dash-card-link" to={to} aria-label={t('card.open', { title })} title={t('card.open', { title })}>
            <ArrowUpRight aria-hidden="true" />
          </Link>
        )}
        {tile.menu}
      </header>
      {children}
    </section>
  );
}

// The figure itself, and under it `chart` (the measure over time) or its split.
export function KpiTile({
  value, format, current, previous, goodWhen, comparison, detail, chart = null, stages, tone,
}) {
  const comparable = previous !== undefined && change(current, previous) !== null;
  return (
    <div className="xo-dash-kpi">
      <strong className={`xo-dash-kpi-value${tone ? ` is-${tone}` : ''}`}>
        {typeof value === 'number' ? <Counter value={value} format={format} /> : value}
      </strong>
      <span className="xo-dash-kpi-meta">
        {previous !== undefined && <Delta current={current} previous={previous} goodWhen={goodWhen} label={comparison} />}
        {comparable && <span>{t('kpi.vs_last_week')}</span>}
        {previous === undefined && detail && <span>{detail}</span>}
      </span>
      {chart}
      {!chart && stages && <div className="xo-dash-kpi-stages"><StageBar stages={stages} compact /></div>}
    </div>
  );
}

// Small figures, each a number over its word.
function Figures({ items, className }) {
  return (
    <ul className={className}>
      {items.map((item) => (
        <li key={item.label} className={item.tone ? `is-${item.tone}` : ''}>
          <b>{typeof item.value === 'number' ? <Counter value={item.value} format={item.format} /> : item.value}</b>
          <span>{item.label}</span>
        </li>
      ))}
    </ul>
  );
}

// The one large figure at the top of a chart, what it counts under it, and a few
// small figures to its right.
export function Headline({ value, format, caption, delta = null, side = [] }) {
  const small = side.filter(Boolean);
  return (
    <div className="xo-dash-headline">
      <div className="xo-dash-headline-main">
        <strong>{typeof value === 'number' ? <Counter value={value} format={format} /> : value}{delta}</strong>
        {caption && <span>{caption}</span>}
      </div>
      {small.length > 0 && <Figures items={small} className="xo-dash-headline-side" />}
    </div>
  );
}

// ------------------------------------------------------------------ shapes

// A ring filled to a share, with its number in the middle.
export function Ring({ value, total, size = 104, stroke = 8, label, caption, tone = 'navy' }) {
  const reduce = useReducedMotion();
  const radius = (size - stroke) / 2;
  const length = 2 * Math.PI * radius;
  const share = total > 0 ? Math.min(1, Math.max(0, value / total)) : 0;
  return (
    <div className={`xo-dash-ring is-${tone}`} style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={radius} className="xo-dash-ring-track" strokeWidth={stroke} />
        <motion.circle cx={size / 2} cy={size / 2} r={radius} className="xo-dash-ring-fill" strokeWidth={stroke}
          strokeDasharray={length} initial={{ strokeDashoffset: length }} animate={{ strokeDashoffset: length * (1 - share) }}
          transition={reduce ? { duration: 0 } : { duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          transform={`rotate(-90 ${size / 2} ${size / 2})`} strokeLinecap="round" />
      </svg>
      <span className="xo-dash-ring-text"><strong>{label}</strong>{caption && <small>{caption}</small>}</span>
    </div>
  );
}

// Work split by where it is: one bar in parts, each named with its count below.
// `compact` keeps the key to one quiet line of names and counts.
export function StageBar({ stages, total = null, to = null, compact = false }) {
  const sum = total === null ? stages.reduce((all, stage) => all + stage.value, 0) : total;
  return (
    <div className={`xo-dash-stages${compact ? ' is-compact' : ''}`}>
      <div className="xo-dash-stage-bar" role="img" aria-label={stages.map((stage) => `${stage.label} ${stage.value}`).join(', ')}>
        {sum === 0 ? <i className="is-empty" /> : stages.filter((stage) => stage.value > 0).map((stage) => (
          <motion.i key={stage.key} className={`is-${stage.tone || 'navy'}`} initial={false}
            animate={{ flexGrow: stage.value }} transition={{ duration: 0.5, ease: 'easeOut' }} style={{ flexGrow: stage.value }} />
        ))}
      </div>
      <ol className="xo-dash-stage-key">
        {stages.map((stage) => {
          const inner = (
            <>
              <i className={`is-${stage.tone || 'navy'}`} aria-hidden="true" />
              <span>{stage.label}</span>
              <strong><Counter value={stage.value} /></strong>
            </>
          );
          return <li key={stage.key}>{to ? <Link to={stage.to || to}>{inner}</Link> : inner}</li>;
        })}
      </ol>
    </div>
  );
}

// Counts side by side when they are not parts of one whole (work waiting next to
// everything ever finished): each its own number over its word, divided by hairlines.
export function Tally({ items, to = null }) {
  return (
    <ol className="xo-dash-tally">
      {items.map((item) => {
        const inner = (
          <>
            <strong><Counter value={item.value} /></strong>
            <span><i aria-hidden="true" />{item.label}</span>
          </>
        );
        const target = item.to || to;
        return <li key={item.key} className={`is-${item.tone || 'navy'}`}>{target ? <Link to={target}>{inner}</Link> : <div>{inner}</div>}</li>;
      })}
    </ol>
  );
}

export function ShareBar({ value, total, tone = 'navy' }) {
  const width = total > 0 ? Math.max(value > 0 ? 3 : 0, (value / total) * 100) : 0;
  return <span className={`xo-dash-share is-${tone}`} aria-hidden="true"><motion.i initial={false} animate={{ width: `${width}%` }} transition={{ duration: 0.5 }} style={{ width: `${width}%` }} /></span>;
}

// ------------------------------------------------------------------ rows

export function RowList({ children }) {
  return <ol className="xo-dash-rows">{children}</ol>;
}

// One line of a list: what it is, a detail under it, and its state and time at
// the end. A lead is drawn only where it says something (a flag, a device, a
// state); `dot` marks a row with a small coloured state dot instead.
export function Row({ lead = null, dot = null, title, detail, meta, pill = null, to = null, state = undefined }) {
  const body = (
    <>
      {lead || (dot && <span className={`xo-dash-row-dot is-${dot}`} aria-hidden="true" />)}
      <span className="xo-dash-row-main">
        <strong>{title}</strong>
        {detail && <small>{detail}</small>}
      </span>
      <span className="xo-dash-row-side">
        {pill}
        {meta && <small>{meta}</small>}
      </span>
    </>
  );
  const className = lead || dot ? 'has-lead' : '';
  return (
    <motion.li layout="position" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
      {to ? <Link to={to} state={state} className={className}>{body}</Link> : <div className={className}>{body}</div>}
    </motion.li>
  );
}

export function Tag({ tone = 'flat', children }) {
  return <span className={`xo-dash-tag is-${tone}`}>{children}</span>;
}

// A few numbers side by side, each with its word under it.
export function Facts({ items }) {
  return <Figures items={items.filter(Boolean)} className="xo-dash-facts" />;
}

export function ChartKey({ items }) {
  return (
    <div className="xo-dash-key">
      {items.map((item) => <span key={item.label}><i className={item.line ? 'is-line' : ''} style={{ background: item.color }} />{item.label}</span>)}
    </div>
  );
}
